import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  getCurrentAssignment,
  saveAnswer,
  submitAssignment,
  type CurrentAssignment,
  type GivenAnswer,
} from "../../services/me-api";

function formatRemaining(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

/** FR-007/FR-008/FR-009/FR-014: the test-taking UI — question-by-question navigation with
 * autosaved answers (FR-008), an optional countdown that auto-submits at zero (FR-014), and a
 * final submit that the member can't undo (FR-009). */
export function TakeTestPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [assignment, setAssignment] = useState<CurrentAssignment | null | "loading">("loading");
  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState<Record<string, GivenAnswer>>({});
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  // Answer saves fire from onChange/onBlur handlers without the caller awaiting them (e.g. a
  // radio's onChange can't be async-awaited by the click that triggered it) — tracked here so
  // Submit can wait for every in-flight save to land before finalizing, instead of racing one.
  const pendingSaves = useRef<Set<Promise<unknown>>>(new Set());

  useEffect(() => {
    getCurrentAssignment().then((current) => {
      if (!current) {
        navigate(`/company/${slug}/result`, { replace: true });
        return;
      }
      setAssignment(current);
      const initial: Record<string, GivenAnswer> = {};
      for (const [questionId, a] of Object.entries(current.answers)) initial[questionId] = a.given;
      setAnswers(initial);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const deadline = useMemo(() => {
    if (!assignment || assignment === "loading" || !assignment.timeLimitMinutes || !assignment.startedAt) {
      return null;
    }
    return assignment.startedAt + assignment.timeLimitMinutes * 60_000;
  }, [assignment]);

  const [remainingMs, setRemainingMs] = useState<number | null>(deadline ? deadline - Date.now() : null);

  async function handleSubmit() {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    try {
      await Promise.all(pendingSaves.current);
      await submitAssignment();
      navigate(`/company/${slug}/result`, { replace: true });
    } finally {
      setSubmitting(false);
    }
  }

  useEffect(() => {
    if (!deadline) return;
    const interval = setInterval(() => {
      const remaining = deadline - Date.now();
      setRemainingMs(remaining);
      if (remaining <= 0) {
        clearInterval(interval);
        handleSubmit();
      }
    }, 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deadline]);

  if (assignment === "loading") return <div className="p-6 text-body">Loading…</div>;
  if (!assignment) return null; // navigating to result

  const question = assignment.questions[index];
  if (!question) return null;

  async function updateAnswer(questionId: string, value: GivenAnswer) {
    setAnswers((a) => ({ ...a, [questionId]: value }));
    const promise = saveAnswer(questionId, value);
    pendingSaves.current.add(promise);
    try {
      await promise;
    } finally {
      pendingSaves.current.delete(promise);
    }
  }

  function toggleMultiSelect(option: string) {
    const current = answers[question!.id];
    const arr = Array.isArray(current) ? current : [];
    const next = arr.includes(option) ? arr.filter((o) => o !== option) : [...arr, option];
    updateAnswer(question!.id, next);
  }

  return (
    <div className="mx-auto max-w-2xl p-6">
      <div className="mb-4 flex items-center justify-between">
        <span className="text-sm text-body" aria-live="polite">
          Question {index + 1} of {assignment.questions.length}
        </span>
        {remainingMs !== null && (
          <span
            className="rounded bg-surface-alt px-3 py-1 text-sm font-sans text-ink"
            role="timer"
            aria-label={`Time remaining: ${formatRemaining(remainingMs)}`}
          >
            Time remaining: {formatRemaining(remainingMs)}
          </span>
        )}
      </div>

      <h1 className="mb-4 font-sans text-lg text-ink">{question.text}</h1>

      {(question.type === "single_choice" || question.type === "true_false") && (
        <div className="flex flex-col gap-2">
          {(question.options ?? []).map((option) => (
            <label key={option} className="flex items-center gap-2 text-sm text-body">
              <input
                type="radio"
                name={question.id}
                checked={answers[question.id] === option}
                onChange={() => updateAnswer(question.id, option)}
              />
              {option === "true" ? "True" : option === "false" ? "False" : option}
            </label>
          ))}
        </div>
      )}

      {question.type === "multi_select" && (
        <div className="flex flex-col gap-2">
          {(question.options ?? []).map((option) => (
            <label key={option} className="flex items-center gap-2 text-sm text-body">
              <input
                type="checkbox"
                checked={Array.isArray(answers[question.id]) && (answers[question.id] as string[]).includes(option)}
                onChange={() => toggleMultiSelect(option)}
              />
              {option}
            </label>
          ))}
        </div>
      )}

      {question.type === "free_text" && (
        <textarea
          value={(answers[question.id] as string) ?? ""}
          onChange={(e) => setAnswers((a) => ({ ...a, [question.id]: e.target.value }))}
          onBlur={(e) => updateAnswer(question.id, e.target.value)}
          rows={5}
          className="w-full rounded border border-surface-alt px-3 py-2"
        />
      )}

      <div className="mt-6 flex items-center justify-between">
        <button
          onClick={() => setIndex((i) => Math.max(0, i - 1))}
          disabled={index === 0}
          className="rounded bg-surface-alt px-4 py-2 text-sm disabled:opacity-50"
        >
          ← Previous
        </button>
        {index < assignment.questions.length - 1 ? (
          <button
            onClick={() => setIndex((i) => Math.min(assignment.questions.length - 1, i + 1))}
            className="rounded bg-brand px-4 py-2 font-sans text-surface"
          >
            Next →
          </button>
        ) : (
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className="rounded bg-brand px-4 py-2 font-sans text-surface disabled:opacity-50"
          >
            {submitting ? "Submitting…" : "Submit test"}
          </button>
        )}
      </div>
    </div>
  );
}
